import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const ToastContext = createContext(null);

// Глобальный объект для вызова извне React-компонентов
export const toastAPI = {
  success: () => {},
  error: () => {},
  warning: () => {},
  info: () => {},
  removeToast: () => {},
  clearAll: () => {},
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    setToasts(currentToasts => {
      // Ищем тост с таким же типом, заголовком и текстом
      const duplicateIndex = currentToasts.findIndex(
        t => t.type === toast.type && t.title === toast.title && t.text === toast.text
      );

      if (duplicateIndex > -1) {
        // Если дубликат найден, берем его и увеличиваем счетчик
        const newToasts = [...currentToasts];
        newToasts[duplicateIndex] = {
          ...newToasts[duplicateIndex],
          count: (newToasts[duplicateIndex].count || 1) + 1,
          timestamp: Date.now()
        };
        return newToasts;
      }

      // Если ограничение на количество (например, 5), удаляем самый старый
      const maxToasts = 5;
      const newToast = {
        id: Math.random().toString(36).substr(2, 9),
        count: 1,
        timestamp: Date.now(),
        duration: 5000, 
        ...toast
      };

      const results = [...currentToasts, newToast];
      if (results.length > maxToasts) {
        results.shift(); 
      }
      return results;
    });
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(currentToasts => currentToasts.map(t => 
      t.id === id ? { ...t, isLeaving: true } : t
    ));

    // Даем время на анимацию закрытия
    setTimeout(() => {
      setToasts(currentToasts => currentToasts.filter(t => t.id !== id));
    }, 300);
  }, []);

  const clearAll = useCallback(() => {
    setToasts(currentToasts => currentToasts.map(t => ({ ...t, isLeaving: true })));
    setTimeout(() => {
      setToasts([]);
    }, 300);
  }, []);

  const value = {
    toasts,
    success: (text, title, options) => addToast({ type: 'ok', title, text, ...options }),
    error: (text, title, options) => addToast({ type: 'err', title, text, ...options }),
    warning: (text, title, options) => addToast({ type: 'warn', title, text, ...options }),
    info: (text, title, options) => addToast({ type: 'info', title, text, ...options }),
    removeToast,
    clearAll
  };

  // Привязываем методы контекста к глобальному API для поддержки вызовов вне компонентов
  useEffect(() => {
    toastAPI.success = value.success;
    toastAPI.error = value.error;
    toastAPI.warning = value.warning;
    toastAPI.info = value.info;
    toastAPI.removeToast = value.removeToast;
    toastAPI.clearAll = value.clearAll;
  }, [value.success, value.error, value.warning, value.info, value.removeToast, value.clearAll]);

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast используйте только внутри ToastProvider');
  }
  return context;
};
